import {Button} from 'components/system/button';
import {Checkbox} from 'components/system/checkbox';
import {Divider} from 'components/system/divider';
import {TextInput} from 'components/system/text_input';
import {useOutsideClick} from 'hooks/outside_click';
import {ChevronDownIcon, ChevronUpIcon} from 'icons';
import {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {classNames} from 'utils';
import {slugify} from 'utils/string';
import {DropdownContainer} from './dropdown_container';
import styles from './filter_two_level_dropdown.module.css';

export function FilterTwoLevelDropdown<T = any>({
  placeholder,
  topLevelOptions,
  value = [],
  disabled = false,
  enableSearch = false,
  searchPlaceholder = 'Filter options...',
  onChange,
  allowOpen,
}: Props<T>) {
  const ref = useRef<HTMLDivElement>();
  const [opened, setOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [upperLevelsState, setUpperLevelsState] = useState(
    topLevelOptions.map((topLevelOption) => ({
      value: topLevelOption.value,
      label: topLevelOption.label,
      options: topLevelOption.options,
      checked: value.includes(topLevelOption.value),
      opened: false,
    })),
  );

  useEffect(() => {
    setUpperLevelsState(
      topLevelOptions.map((topLevelOption) => ({
        value: topLevelOption.value,
        label: topLevelOption.label,
        options: topLevelOption.options,
        checked: value.includes(topLevelOption.value),
        opened: false,
      })),
    );
  }, [topLevelOptions]);

  useOutsideClick(ref.current, () => {
    setOpened(false);
  });

  const bareOptions = useMemo(() => {
    return topLevelOptions.map((topLevelOption) => topLevelOption.options).flat();
  }, [topLevelOptions]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return bareOptions;
    const slugifiedSearchQuery = slugify(searchQuery);
    return bareOptions.filter((option) => slugify(option.label).includes(slugifiedSearchQuery));
  }, [bareOptions, searchQuery]);

  const anyFilteredOptionsAreSelected = useMemo(() => {
    return filteredOptions.filter((option) => value.includes(option.value)).length !== 0;
  }, [value, filteredOptions]);

  const handleToggleClick = () => {
    if (!allowOpen || allowOpen()) setOpened((opened) => !opened);
  };

  const handleOptionChange = (option: Option<T>, type: 'top' | 'inner') => (checked: boolean) => {
    if (type === 'top') {
      const upper = upperLevelsState.find((category) => category.value === option.value);
      const upperIndex = upperLevelsState.findIndex((category) => category.value === option.value);
      if (checked) {
        onChange([
          ...value,
          ...upper.options.map((option) => option.value).filter((v) => !value.includes(v)),
        ]);
        setUpperLevelsState((oldState) => {
          const newState = [...oldState];
          newState[upperIndex] = {
            ...upper,
            opened: true,
          };
          return newState;
        });
      } else {
        onChange(value.filter((v) => !upper.options.map((option) => option.value).includes(v)));

        setUpperLevelsState((oldState) => {
          const newState = [...oldState];
          newState[upperIndex] = {
            ...upper,
            opened: false,
          };
          return newState;
        });
      }
    } else if (type === 'inner') {
      if (checked) {
        if (value.includes(option.value)) return;
        onChange([...value, option.value]);
      } else {
        onChange(value.filter((v) => v !== option.value));
      }
    }
  };

  const handleCheckAll = () => {
    onChange([...new Set([...value, ...filteredOptions.map(({value}) => value)])]);
  };

  const handleClear = () => {
    const valuesToClear = filteredOptions.map((option) => option.value);
    onChange(value.filter((v) => !valuesToClear.includes(v)));
    setUpperLevelsState((oldState) => {
      return oldState.map((upper) => {
        return {...upper, opened: false};
      });
    });
  };

  return (
    <div className={styles.container} ref={ref}>
      <Button
        title={`Filter by ${placeholder.toLocaleLowerCase()}`}
        display='block'
        size='sm'
        type='outline'
        color={value.length > 0 ? 'orange' : 'greyMedium'}
        disabled={disabled}
        onClick={handleToggleClick}
        icon={
          <ChevronDownIcon
            color={value.length > 0 ? 'orange' : 'greyDark'}
            className={classNames([styles.icon, opened && styles.opened])}
          />
        }
        iconPlacement='right'>
        {placeholder}
      </Button>
      <DropdownContainer opened={opened} className={styles.dropdown}>
        {enableSearch && (
          <>
            <TextInput
              size='sm'
              value={searchQuery}
              placeholder={searchPlaceholder}
              onChange={setSearchQuery}
            />
            <Divider />
          </>
        )}
        <div>
          {upperLevelsState.map((topLevelOption) => (
            <Fragment key={topLevelOption.label}>
              <div className={styles.topLevelCheckboxes}>
                <Checkbox
                  key={topLevelOption.label}
                  label={topLevelOption.label}
                  value={
                    topLevelOption.options.filter((option) => value.includes(option.value))
                      .length == topLevelOption.options.length
                  }
                  onChange={onChange && handleOptionChange(topLevelOption, 'top')}
                />
                <div className={styles.dropButtonTopLevel}>
                  <Button
                    title={topLevelOption.opened ? 'Collapse' : 'Expand'}
                    name='toggle'
                    type='icon'
                    onClick={() => {
                      setUpperLevelsState(
                        upperLevelsState.map((upperLevelOption) => {
                          if (upperLevelOption.value === topLevelOption.value) {
                            return {...upperLevelOption, opened: !upperLevelOption.opened};
                          } else {
                            return upperLevelOption;
                          }
                        }),
                      );
                    }}>
                    {topLevelOption.opened ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </Button>
                </div>
              </div>
              <div className={styles.innerCheckboxes}>
                {topLevelOption.opened &&
                  topLevelOption.options.map((option) => (
                    <Checkbox
                      key={option.label}
                      label={
                        option.amount ? `${option.label} (${option.amount})` : `${option.label}`
                      }
                      value={value.includes(option.value)}
                      onChange={onChange && handleOptionChange(option, 'inner')}
                    />
                  ))}
              </div>
            </Fragment>
          ))}
        </div>
        {filteredOptions.length > 0 && (
          <>
            <Divider />
            <div className={styles.bottomOptions}>
              <Button type='text' onClick={handleCheckAll}>
                Check all
              </Button>
              {anyFilteredOptionsAreSelected && (
                <Button type='text' onClick={handleClear}>
                  Clear
                </Button>
              )}
            </div>
          </>
        )}
      </DropdownContainer>
    </div>
  );
}

type Props<T> = {
  placeholder: string;
  topLevelOptions: TopLevelOptions<T>[];
  value?: T[];
  disabled?: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  onChange?: (value: T[]) => void;
  allowOpen?: () => boolean;
};

type TopLevelOptions<T> = {
  label: string;
  value: T;
  options?: Option<T>[];
};

type Option<T> = {
  value: T;
  label: string;
  amount?: number;
};
